import { useState, useEffect, useRef } from 'react';
import axiosInstance from '@/utils/axios';
import { Input } from '@/components/ui/input';
import { Search, User, Calendar, Building2 } from 'lucide-react';

export default function PatientSearchAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "🔍 Buscar paciente por nome...",
  healthUnits = [],
  className = ""
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggestions when value changes
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!value || value.length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(`/patients/search/autocomplete?q=${encodeURIComponent(value)}&limit=8`);
        setSuggestions(response.data);
        setIsOpen(response.data.length > 0);
        setHighlightedIndex(-1);
      } catch (error) {
        console.error('Erro ao buscar sugestões:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [value]);

  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  const handleSelect = (patient) => {
    if (onSelect) {
      onSelect(patient);
    }
    onChange(patient.nome);
    setIsOpen(false);
    setSuggestions([]);
  };

  const getUnitName = (unitId) => {
    const unit = healthUnits.find(u => u.id === unitId);
    return unit ? unit.nome : null;
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary" />
        <Input
          ref={inputRef}
          data-testid="search-input"
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) setIsOpen(true);
          }}
          className={`pl-12 bg-white border-2 border-primary/20 focus:border-primary focus:bg-white transition-all rounded-lg h-14 text-base font-medium shadow-sm ${className}`}
          autoComplete="off"
        />
        {loading && (
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {isOpen && suggestions.length > 0 && (
        <div 
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto"
          data-testid="autocomplete-suggestions"
        >
          {suggestions.map((patient, index) => (
            <div
              key={patient.id}
              onClick={() => handleSelect(patient)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`px-4 py-3 cursor-pointer transition-colors ${
                index === highlightedIndex 
                  ? 'bg-primary/10 border-l-4 border-l-primary' 
                  : 'hover:bg-gray-50 border-l-4 border-l-transparent'
              }`}
              data-testid={`suggestion-${patient.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold flex-shrink-0">
                  {patient.nome.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">
                    {patient.nome}
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                    {patient.data_nascimento && patient.data_nascimento !== 'N/A' && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {patient.data_nascimento}
                      </span>
                    )}
                    {patient.unit_id && getUnitName(patient.unit_id) && (
                      <span className="flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {getUnitName(patient.unit_id)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
